<template>
  <v-container class="container-form">
    <v-card v-if="errorRegistro" class="pa-5 mb-5 border-error" elevation="2">
      <v-row align="center" no-gutters>
        <v-col cols="4" class="text-center">
          <v-img
            src="@/assets/img/logo.png"
            alt="Logo Aliquam"
            max-width="80"
            class="mx-auto"
          ></v-img>
        </v-col>
        <v-col cols="8" class="pl-4">
          <div class="text-h6 mb-2">Error de registro</div>
          <p class="text-body-2 mb-4">{{ mensajeError }}</p>
          <v-btn
            color="primary"
            block
            @click="cerrarError"
            prepend-icon="mdi-close"
          >
            Cerrar y corregir
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-form v-if="!errorRegistro" ref="form" id="crTramite">
      <div>
        <v-img
          class="logo-index"
          src="@/assets/img/logo.png"
          alt="Logo Aliquam"
          id="logo"
        ></v-img>
        <h4>Sistema de solicitud de Cartas de Autorización</h4>
      </div>

      <v-text-field
        required
        label="RFC"
        v-model="rfc"
        id="vat"
        placeholder="Ingresar RFC"
        variant="outlined"
        :rules="[(v) => !!v || 'El RFC es obligatorio']"
      ></v-text-field>

      <div v-show="showEmailDiv">
        <v-text-field
          required
          label="Nombre(s)"
          v-model="nombre"
          variant="outlined"
          :rules="[(v) => !!v || 'El nombre es obligatorio']"
        ></v-text-field>
        <v-text-field
          required
          label="Apellido Paterno"
          v-model="apellidoPaterno"
          variant="outlined"
          :rules="[(v) => !!v || 'El apellido paterno es obligatorio']"
        ></v-text-field>
        <v-text-field
          required
          label="Apellido Materno"
          v-model="apellidoMaterno"
          variant="outlined"
          :rules="[(v) => !!v || 'El apellido materno es obligatorio']"
        ></v-text-field>
        <v-text-field
          required
          label="Correo Electrónico"
          v-model="email"
          variant="outlined"
          :rules="[
            (v) => !!v || 'El correo es obligatorio',
            (v) => /.+@.+\..+/.test(v) || 'Correo no válido',
          ]"
        ></v-text-field>
        <v-text-field
          required
          label="Celular"
          v-model="phone"
          variant="outlined"
          :rules="[(v) => !!v || 'El celular es obligatorio']"
        ></v-text-field>
        <v-text-field
          label="Especialidad"
          v-model="speciality"
          variant="outlined"
        ></v-text-field>
      </div>

      <div class="checkbox-wrapper">
        <v-checkbox v-model="privacyAccepted" id="checkbox">
          <template v-slot:label>
            He leído y acepto el
            <a
              href="https://servicios.aliquam.app/facturacion/static/aviso_privacidad.pdf"
              target="_blank"
              >aviso de privacidad</a
            >.
          </template>
        </v-checkbox>
      </div>

      <v-btn
        @click="checkRfc"
        class="kiosko-btn"
        v-show="!showAccountButton"
        :disabled="loading"
      >
        Acceder
      </v-btn>

      <div class="loader-container" v-if="loading">
        <v-progress-circular
          color="primary"
          indeterminate
          :size="45"
          :width="10"
        ></v-progress-circular>
      </div>

      <v-btn
        v-show="showAccountButton"
        @click="createAccount"
        class="kiosko-btn"
        :disabled="loading"
      >
        Crear cuenta
      </v-btn>
    </v-form>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { createDoctor, getDoctor, isExCliente } from "@/router/services/apiService";

const form = ref(null);
const showEmailDiv = ref(false);
const showAccountButton = ref(false);
const rfc = ref("");
const nombre = ref("");
const apellidoPaterno = ref("");
const apellidoMaterno = ref("");
const email = ref("");
const phone = ref("");
const speciality = ref("");
const privacyAccepted = ref(false);
const loading = ref(false);

// NUEVOS ESTADOS PARA EL ERROR
const errorRegistro = ref(false);
const mensajeError = ref("");

const router = useRouter();

const cerrarError = () => {
  errorRegistro.value = false;
  mensajeError.value = "";
};

const checkRfc = async () => {
  if (!rfc.value) {
    alert("Por favor ingresa tu RFC");
    return;
  }
  if (!privacyAccepted.value) {
    alert("Debes aceptar el aviso de privacidad");
    return;
  }

  loading.value = true;
  errorRegistro.value = false; // Limpiar errores previos

  try {
    const data = await getDoctor(rfc.value);
    if (data && data.length > 0) {
      const doctor = data[0];

      // Médico ExCliente o registro de la webapp: puede solicitar la CDA.
      // Médico cliente activo: debe generar el trámite desde la app.
      if (doctor.medical_type === "medico" && !isExCliente(doctor)) {
        router.push(`/existe_${doctor.vat}`);
      } else if (
        doctor.medical_type === "medico" ||
        doctor.medical_type === "webapp_cda"
      ) {
        router.push(`/solicitud/${doctor.vat}`);
      } else {
        showEmailDiv.value = true;
        showAccountButton.value = true;
      }
    } else {
      showEmailDiv.value = true;
      showAccountButton.value = true;
    }
  } catch (error) {
    console.error("Error:", error);
    mensajeError.value = "Error al consultar el RFC. Intenta de nuevo.";
    errorRegistro.value = true;
  } finally {
    loading.value = false;
  }
};

const createAccount = async () => {
  if (!privacyAccepted.value) {
    alert("Debes aceptar el aviso de privacidad");
    return;
  }

  if (
    !rfc.value ||
    !nombre.value ||
    !apellidoPaterno.value ||
    !apellidoMaterno.value ||
    !email.value ||
    !phone.value
  ) {
    alert("Por favor completa todos los campos obligatorios");
    return;
  }

  loading.value = true;
  errorRegistro.value = false;

  const doctorData = {
    vat: rfc.value.trim().toUpperCase(),
    name: `${nombre.value} ${apellidoPaterno.value} ${apellidoMaterno.value}`
      .trim()
      .toUpperCase(),
    x_last_name_m: apellidoMaterno.value.trim().toUpperCase(),
    x_last_name_p: apellidoPaterno.value.trim().toUpperCase(),
    x_first_names: nombre.value.trim().toUpperCase(),
    email: email.value.trim().toLowerCase(),
    phone: phone.value.trim(),
    function: speciality.value.trim().toUpperCase(),
  };

  try {
    const result = await createDoctor(doctorData);

    if (result && result.success) {
      router.push(`/solicitud/${doctorData.vat}`);
    } else {
      // CAPTURAMOS EL MENSAJE DEL API Y ACTIVAMOS LA CARD
      mensajeError.value =
        result.message || "No se pudo crear la cuenta. Verifica tus datos.";
      errorRegistro.value = true;
    }
  } catch (error) {
    console.error("Error en createAccount:", error);
    mensajeError.value =
      error.message || "Ocurrió un error inesperado al crear la cuenta.";
    errorRegistro.value = true;
  } finally {
    loading.value = false;
  }
};
</script>

<style>
.small-container-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

.loader-container {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.alert-box {
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
}

.alert-box.success {
  background-color: #dff0d8;
  color: #3c763d;
}

.alert-box.warning {
  background-color: #fcf8e3;
  color: #8a6d3b;
}

.alert-box.error {
  background-color: #f2dede;
  color: #a94442;
}

.alert-box.info {
  background-color: #d9edf7;
  color: #31708f;
}

.loader {
  border: 8px solid #f3f3f3;
  border-radius: 50%;
  border-top: 8px solid #3498db;
  width: 60px;
  height: 60px;
  animation: spin 2s linear infinite;
  margin: 20px auto;
}

.border-error {
  border-left: 5px solid #ff5252 !important; /* Rojo de error */
}

.text-h6 {
  font-weight: bold;
}

.container-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
